import { useState } from 'preact/hooks';
import type { CampaignPromo, OnsiteProduct, OnsitePrice, CheckoutError, CheckoutCompletionResponse, CheckoutSuccessDetails, TrialDiscount } from '@/types';
import { CheckoutShell } from '@/components/CheckoutShell';
import { OrderSummary } from '@/components/OrderSummary';
import { TrialPaymentForm, trialSubtitle } from '@/components/TrialPaymentForm';
import { t, useT } from '@/i18n/t';

interface Props {
  product?: OnsiteProduct;
  price?: OnsitePrice;
  // Days of free trial before the first charge; drives the order summary copy.
  trialPeriodDays?: number;
  // SetupIntent client secret + account: the trial captures a card without
  // charging it (no Checkout Session), so we mount the Payment Element on the
  // SetupIntent and confirm it with stripe.confirmSetup.
  clientSecret: string;
  // See TrialPaymentForm.customerSessionClientSecret.
  customerSessionClientSecret?: string;
  publishableKey: string;
  connectedAccount?: string;
  // Server-masked checkout email (ezAuth path only); display-only.
  maskedEmail?: string;
  // The visitor's raw email when the flow knows it (guest paths); forwarded to
  // the Payment Element as a billing-details default for prefill and Link
  // recognition.
  email?: string;
  // Renders a "Not you?" switch next to the identity note. Absent hides it
  // (guests, and BYO domains whose adapter has no logout).
  onSwitchIdentity?: () => void;
  // Whether the domain has an active promo code, so the promo entry renders. The
  // trial has no Checkout Session, so an entered code is checked via
  // validatePromoCode and attached at confirm rather than by a session action.
  promotionCodesEnabled?: boolean;
  // Mounts the Express Checkout Element (setup mode) as the wallet surface
  // above the card form (the Payment Element's own wallet buttons are
  // suppressed).
  showExpressCheckout?: boolean;
  // False hides every wallet surface (see CheckoutFlow walletsEnabled).
  walletsEnabled?: boolean;
  // Fired once when the Express Checkout Element reports ready; the flow
  // beacons it as the wallets_ready timing marker.
  onWalletsReady?: () => void;
  // Creates the trialing subscription on the confirmed SetupIntent's card. The
  // flow owns this (it holds the credential + identifiers); this step hands back
  // the confirmed SetupIntent id, the applied promo code (or null), and — on
  // the deferred-identity path — the email collected by the form's elements.
  // A trial-gate ineligibleReason ('trial_card_used' /
  // 'trial_already_redeemed') is a soft denial routed to onTrialIneligible
  // rather than treated as success.
  onConfirm: (setupIntentId: string, promoCode: string | null, email?: string) => Promise<CheckoutCompletionResponse>;
  // Fired when the visitor applies or removes a promo code (null on removal),
  // so the owning flow can attach it to later funnel events.
  onPromoChange?: (code: string | null) => void;
  // The mount's campaign code, auto-applied by the form (see
  // TrialPaymentForm.campaignPromo).
  campaignPromo?: CampaignPromo | null;
  // Fired when the campaign code's auto-apply succeeds.
  onCampaignApply?: () => void;
  // Trial created and provisioned; carries the backend completion (whose email
  // the success screen reads on the deferred-identity path) and the applied
  // promo (which the success screen shows as chip + discount note).
  onSuccess: (completion: CheckoutCompletionResponse, details?: CheckoutSuccessDetails) => void;
  // Trial-gate soft denial (Gate 2's reused card, or Gate 1's already-trialed
  // visitor at confirm). The card is already saved either way, so the flow
  // offers convert-to-paid on it — the id is handed back, along with the
  // applied promo code and its validated discount (or nulls).
  onTrialIneligible: (setupIntentId: string, promoCode: string | null, discount: TrialDiscount | null) => void;
  // Park/settle around the card confirm (see TrialPaymentForm).
  onSetupConfirmStart?: (setupIntentId: string, promoCode: string | null, discount: TrialDiscount | null) => void;
  onSetupConfirmSettled?: (setupIntentId: string) => void;
  // Reports a failed attempt to the publisher. The step stays open for retry, so
  // this may fire more than once.
  onError?: (error: CheckoutError) => void;
  // Optional: omitted where there is no earlier screen to return to (a direct
  // openCheckout for an authenticated visitor has no plan picker or email form
  // behind the trial card step).
  onBack?: () => void;
  onDismiss?: () => void;
}

export function TrialPaymentStep({
  product,
  price,
  trialPeriodDays,
  clientSecret,
  customerSessionClientSecret,
  publishableKey,
  connectedAccount,
  maskedEmail,
  email,
  onSwitchIdentity,
  promotionCodesEnabled,
  showExpressCheckout,
  walletsEnabled,
  onWalletsReady,
  onConfirm,
  onPromoChange,
  campaignPromo,
  onCampaignApply,
  onSuccess,
  onTrialIneligible,
  onSetupConfirmStart,
  onSetupConfirmSettled,
  onError,
  onBack,
  onDismiss,
}: Props) {
  useT();
  // Reported up by the form so the sidebar previews the applied coupon (the
  // trial has no live session to recompute totals from).
  const [appliedDiscount, setAppliedDiscount] = useState<TrialDiscount | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The promo entry's sidebar mount point: it drives the order summary's
  // discount preview, so it belongs with the summary — the form portals it
  // here (see TrialPaymentForm.promoTarget).
  const [promoTarget, setPromoTarget] = useState<HTMLDivElement | null>(null);

  return (
    <CheckoutShell
      ariaLabel={t('trialPayment.startFreeTrial')}
      currentStep={2}
      sidebar={(
        <>
          <OrderSummary product={product} price={price} amountCentsOverride={undefined} trialPeriodDays={trialPeriodDays} discount={appliedDiscount} />
          <div ref={setPromoTarget} />
        </>
      )}
      wide
      onBack={onBack}
      backDisabled={submitting}
      onDismiss={onDismiss}
    >
      <div class="sm-eyebrow">{t('trialPayment.eyebrow')}</div>
      <h2 class="sm-title">{t('trialPayment.title')}</h2>
      <p class="sm-subtitle">{trialSubtitle(trialPeriodDays)}</p>

      {maskedEmail && (
        <p class="sm-identity-note">
          {t('trialPayment.startingAs')} <strong>{maskedEmail}</strong>
          {onSwitchIdentity && (
            <>
              {' · '}
              <button type="button" class="sm-identity-change" onClick={onSwitchIdentity}>{t('paywall.notYou')}</button>
            </>
          )}
        </p>
      )}

      <TrialPaymentForm
        product={product}
        price={price}
        clientSecret={clientSecret}
        customerSessionClientSecret={customerSessionClientSecret}
        publishableKey={publishableKey}
        connectedAccount={connectedAccount}
        promotionCodesEnabled={promotionCodesEnabled}
        promoTarget={promoTarget}
        email={email}
        showExpressCheckout={showExpressCheckout}
        walletsEnabled={walletsEnabled}
        onWalletsReady={onWalletsReady}
        onConfirm={onConfirm}
        onPromoChange={onPromoChange}
        campaignPromo={campaignPromo}
        onCampaignApply={onCampaignApply}
        onDiscountChange={setAppliedDiscount}
        onSubmittingChange={setSubmitting}
        onSuccess={onSuccess}
        onTrialIneligible={onTrialIneligible}
        onSetupConfirmStart={onSetupConfirmStart}
        onSetupConfirmSettled={onSetupConfirmSettled}
        onError={onError}
      />

      <div class="sm-secure" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {t('common.securedByStripe')}
      </div>
    </CheckoutShell>
  );
}
